import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaDownload, FaCheck, FaLink, FaArrowLeft, FaRedo, FaUndo, FaMagnet, FaImage, FaTimes,
  FaLayerGroup, FaPalette, FaShapes, FaSave, FaThLarge, FaSlidersH,
} from 'react-icons/fa';
import { MdFaceRetouchingNatural, MdMovie, MdVideoLibrary } from 'react-icons/md';
import { toast } from 'react-toastify';
import Loader from '../../Components/Loader/Loader';
import { useAuthContext } from '../../hooks/UseAuthContext';
import api from '../../api';
import './talkingheadsstudio.css';
import './th-studio.css';
import { loadLocalTemplates, saveLocalTemplates } from './talkingHeadsPresets';
import {
  listCompositionTemplates,
  saveCompositionTemplate,
  deleteCompositionTemplate,
  normalizeTalkingHeadsTemplate,
  TEMPLATE_TYPES,
} from './compositionTemplates';
import DreamFaceCompositionPanel from './DreamFaceCompositionPanel';
import { useHistory } from './studioKit';
import ThStage from './ThStage';
import {
  BackgroundInspector, CanvasGallery, DecorInspector, DecorList, LayerInspector, TemplatesPanel,
} from './ThInspector';
import {
  LAYER_META,
  OUTPUT_PRESETS,
  buildLayoutConfig,
  buildPremade,
  designFromLayoutConfig,
  modeLayerKeys,
  normalizeDesign,
  reflowDesign,
} from './thCanvas';

const DEFAULT_CANVAS = 'studio_split';

const TABS = [
  { id: 'canvases', label: 'Canvases', icon: <FaThLarge /> },
  { id: 'edit', label: 'Edit', icon: <FaSlidersH /> },
  { id: 'background', label: 'Background', icon: <FaPalette /> },
  { id: 'decor', label: 'Decor', icon: <FaShapes /> },
  { id: 'templates', label: 'Templates', icon: <FaSave /> },
];

const isTyping = (el) => {
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
};

export default function TalkingHeadsStudio() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthContext();

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [compositionOpen, setCompositionOpen] = useState(false);
  const [avatarLinks, setAvatarLinks] = useState({});
  const [resolvedStreamUrls, setResolvedStreamUrls] = useState({});
  const [resolvingBatches, setResolvingBatches] = useState({});
  const [avatarVideoDims, setAvatarVideoDims] = useState({});

  const [savedTemplates, setSavedTemplates] = useState([]);
  const [templateName, setTemplateName] = useState('');
  const [activeTemplateId, setActiveTemplateId] = useState(null);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  // ── canvas design ─────────────────────────────────────────
  const [outputPreset, setOutputPreset] = useState('landscape');
  const W = OUTPUT_PRESETS[outputPreset].w;
  const H = OUTPUT_PRESETS[outputPreset].h;
  const [design, setDesign] = useState(() => buildPremade(DEFAULT_CANVAS, 1920, 1080));
  const history = useHistory(design, setDesign);
  const pristineRef = useRef(true); // untouched premade → rebuild instead of rescale on format change
  const update = useCallback((fn, opts) => { pristineRef.current = false; history.set(fn, opts); }, [history]);

  const [mode, setMode] = useState('narration');
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState('canvases');
  const [snap, setSnap] = useState(true);
  const [linksOpen, setLinksOpen] = useState(false);
  const [contentSample, setContentSample] = useState(null);
  const [bgPreview, setBgPreview] = useState(null);
  const [bgUploading, setBgUploading] = useState(false);
  const sampleInputRef = useRef(null);
  const textRef = useRef(null);
  const initRef = useRef(false);
  const nudgeAtRef = useRef(0);

  // ── job ───────────────────────────────────────────────────
  const loadJob = useCallback(() => {
    if (!jobId || !user?.token) return Promise.resolve();
    return api.get(`/dreamface/jobs/${jobId}`, {
      headers: { Authorization: `Bearer ${user.token}` },
    }).then((r) => {
      setJob(r.data);
      setAvatarLinks((prev) => {
        const prefill = { ...prev };
        (r.data.batches || []).forEach((b) => {
          if (b.avatar_url && !prefill[b.batch_index]) prefill[b.batch_index] = b.avatar_url;
        });
        return prefill;
      });
      return r.data;
    }).catch(() => {
      toast.error('Failed to load job');
      return null;
    });
  }, [jobId, user?.token]);

  useEffect(() => {
    if (!jobId) return;
    setLoading(true);
    loadJob().finally(() => setLoading(false));
  }, [jobId, loadJob]);

  // first load only: restore the job's saved canvas, else a designed default
  useEffect(() => {
    if (!job || initRef.current) return;
    initRef.current = true;
    const orient = OUTPUT_PRESETS[job.orientation] ? job.orientation : 'landscape';
    const restored = designFromLayoutConfig(job.layout_config, orient);
    if (restored) {
      setOutputPreset(restored.preset);
      setDesign(restored.design);
      pristineRef.current = false;
    } else {
      setOutputPreset(orient);
      setDesign(buildPremade(DEFAULT_CANVAS, OUTPUT_PRESETS[orient].w, OUTPUT_PRESETS[orient].h));
      pristineRef.current = true;
    }
    history.reset();
  }, [job, history]);

  useEffect(() => {
    if (job?.status !== 'processing') return undefined;
    const timer = setInterval(() => { loadJob(); }, 15000);
    return () => clearInterval(timer);
  }, [job?.status, loadJob]);

  // ── resolve DreamFace share links → direct MP4 for the preview ──
  useEffect(() => {
    if (!user?.token || !job?.batches) return undefined;
    const timers = [];
    const controllers = new Map();
    job.batches.forEach((batch) => {
      const idx = batch.batch_index;
      const raw = avatarLinks[idx]?.trim();
      if (!raw) {
        setResolvedStreamUrls((prev) => { if (!prev[idx]) return prev; const n = { ...prev }; delete n[idx]; return n; });
        setResolvingBatches((prev) => { if (!prev[idx]) return prev; const n = { ...prev }; delete n[idx]; return n; });
        return;
      }
      const isDreamFace = /dreamfaceapp\.com/i.test(raw);
      if (!isDreamFace && /^https?:\/\//i.test(raw)) {
        setResolvedStreamUrls((prev) => ({ ...prev, [idx]: raw }));
        setResolvingBatches((prev) => ({ ...prev, [idx]: false }));
        return;
      }
      setResolvingBatches((prev) => ({ ...prev, [idx]: true }));
      const timer = setTimeout(async () => {
        const controller = new AbortController();
        controllers.set(idx, controller);
        try {
          const res = await api.post('/dreamface/resolve-preview', { url: raw }, {
            headers: { Authorization: `Bearer ${user.token}` },
            signal: controller.signal,
          });
          setResolvedStreamUrls((prev) => ({ ...prev, [idx]: res.data?.stream_url || raw }));
        } catch (err) {
          if (err?.name !== 'CanceledError' && err?.code !== 'ERR_CANCELED') {
            setResolvedStreamUrls((prev) => ({ ...prev, [idx]: null }));
          }
        } finally {
          setResolvingBatches((prev) => ({ ...prev, [idx]: false }));
        }
      }, 450);
      timers.push(timer);
    });
    return () => {
      timers.forEach(clearTimeout);
      controllers.forEach((c) => c.abort());
    };
  }, [avatarLinks, user?.token, job?.batches]);

  // ── templates ─────────────────────────────────────────────
  const refreshTemplates = useCallback(async () => {
    const rows = await listCompositionTemplates({ type: TEMPLATE_TYPES.TALKING_HEADS });
    const normalized = rows.map(normalizeTalkingHeadsTemplate);
    setSavedTemplates(normalized);
    saveLocalTemplates(user, normalized);
  }, [user]);

  useEffect(() => {
    if (!user?.token) return;
    setTemplatesLoading(true);
    refreshTemplates()
      .catch(() => setSavedTemplates(loadLocalTemplates(user)))
      .finally(() => setTemplatesLoading(false));
  }, [user, refreshTemplates]);

  const templateToDesign = useCallback((tpl) => {
    const parsed = designFromLayoutConfig(tpl.layout_config || tpl, outputPreset);
    if (!parsed) return null;
    const p = OUTPUT_PRESETS[parsed.preset];
    return { design: parsed.design, W: p.w, H: p.h, label: `${p.label} · ${parsed.design.name}` };
  }, [outputPreset]);

  // ── derived ───────────────────────────────────────────────
  const layoutConfig = useMemo(() => buildLayoutConfig(design, outputPreset), [design, outputPreset]);

  const primaryAvatarStreamUrl = job?.batches?.map((b) => resolvedStreamUrls[b.batch_index]).find(Boolean) || null;
  const primaryAvatarDims = job?.batches?.map((b) => avatarVideoDims[b.batch_index]).find(Boolean) || null;
  const avatarLinkResolving = job?.batches?.some((b) => resolvingBatches[b.batch_index] && avatarLinks[b.batch_index]?.trim()) ?? false;

  const media = useMemo(() => ({
    avatarUrl: primaryAvatarStreamUrl,
    contentSample,
    clipSample: null,
    bgPreview,
    onAvatarMeta: (e) => {
      const v = e.currentTarget;
      if (!v?.videoWidth) return;
      const batchIdx = job?.batches?.find((b) => resolvedStreamUrls[b.batch_index] === primaryAvatarStreamUrl)?.batch_index;
      if (batchIdx != null) {
        setAvatarVideoDims((p) => (p[batchIdx]?.w === v.videoWidth ? p : { ...p, [batchIdx]: { w: v.videoWidth, h: v.videoHeight } }));
      }
    },
  }), [primaryAvatarStreamUrl, contentSample, bgPreview, job?.batches, resolvedStreamUrls]);

  // ── canvas actions ────────────────────────────────────────
  const applyPremade = useCallback((id) => {
    const next = buildPremade(id, W, H);
    history.set(next, { commit: true });
    pristineRef.current = true;
    setActiveTemplateId(null);
    setSelected(null);
    if (!modeLayerKeys(next, mode).length) setMode('narration');
  }, [W, H, history, mode]);

  const changeOutput = useCallback((preset) => {
    if (preset === outputPreset) return;
    const p = OUTPUT_PRESETS[preset];
    const next = pristineRef.current && design.canvasId !== 'custom'
      ? buildPremade(design.canvasId, p.w, p.h)
      : reflowDesign(design, outputPreset, preset);
    setOutputPreset(preset);
    history.set(next, { commit: true });
    if (job?.orientation && preset !== job.orientation) {
      toast.info(`Rendering at ${p.long}. This job was scripted for ${job.orientation}.`);
    }
  }, [outputPreset, design, history, job?.orientation]);

  const select = useCallback((sel, switchMode = false) => {
    setSelected(sel);
    if (sel) {
      setTab('edit');
      if (switchMode && sel.kind === 'layer') setMode(sel.id === 'clip' ? 'clip' : 'narration');
    }
  }, []);

  const switchMode = (m) => {
    setMode(m);
    if (selected?.kind === 'layer' && !modeLayerKeys(design, m).includes(selected.id)) setSelected(null);
  };

  const handleBgUpload = async (file, type) => {
    setBgPreview(URL.createObjectURL(file));
    setBgUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('type', type);
      const res = await api.post('/dreamface/upload-background', fd, {
        headers: { Authorization: `Bearer ${user.token}`, 'Content-Type': 'multipart/form-data' },
      });
      update((d) => ({ ...d, background: { ...d.background, type, url: res.data.url } }), { commit: true });
      toast.success(`Background ${type} uploaded`);
    } catch {
      toast.error(`Failed to upload background ${type}`);
      setBgPreview(null);
    } finally {
      setBgUploading(false);
    }
  };

  // ── keyboard ──────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      if (compositionOpen || isTyping(document.activeElement)) return;
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) history.redo(); else history.undo();
        return;
      }
      if (mod && e.key.toLowerCase() === 'y') { e.preventDefault(); history.redo(); return; }
      if (!selected) return;
      if (e.key === 'Escape') { setSelected(null); return; }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selected.kind === 'decor') {
        e.preventDefault();
        update((d) => ({ ...d, decor: d.decor.filter((x) => x.id !== selected.id) }), { commit: true });
        setSelected(null);
        return;
      }
      const step = e.shiftKey ? 10 : 1;
      const delta = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[e.key];
      if (!delta) return;
      e.preventDefault();
      // a burst of arrow presses is one undo step, separate from the drag before it
      if (Date.now() - nudgeAtRef.current > 700) history.checkpoint();
      nudgeAtRef.current = Date.now();
      update((d) => {
        if (selected.kind === 'layer') {
          const l = d.layers[selected.id];
          if (!l) return d;
          return {
            ...d,
            layers: {
              ...d.layers,
              [selected.id]: {
                ...l,
                x: Math.max(0, Math.min(W - l.width, l.x + delta[0])),
                y: Math.max(0, Math.min(H - l.height, l.y + delta[1])),
              },
            },
          };
        }
        return {
          ...d,
          decor: d.decor.map((x) => (x.id === selected.id
            ? { ...x, xPct: (x.xPct ?? 0) + (delta[0] / W) * 100, yPct: (x.yPct ?? 0) + (delta[1] / H) * 100 }
            : x)),
        };
      });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected, history, update, W, H, compositionOpen]);

  // ── templates ─────────────────────────────────────────────
  const saveTemplate = async (asNew = false) => {
    const name = templateName.trim() || design.name;
    if (!name) { toast.error('Enter a template name'); return; }
    try {
      const res = await saveCompositionTemplate({
        type: TEMPLATE_TYPES.TALKING_HEADS,
        name,
        payload: { layout_config: layoutConfig },
        templateId: (!asNew && activeTemplateId) || undefined,
      });
      const tid = res?.template_id || (!asNew && activeTemplateId) || null;
      toast.success(!asNew && activeTemplateId ? 'Template updated' : 'Template saved to your account');
      setActiveTemplateId(tid);
      setTemplateName(name);
      await refreshTemplates();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save template');
    }
  };

  const loadTemplate = (tpl) => {
    const parsed = designFromLayoutConfig(tpl.layout_config || tpl, outputPreset);
    if (!parsed) { toast.error('This template could not be read'); return; }
    setOutputPreset(parsed.preset);
    history.set(normalizeDesign(parsed.design, OUTPUT_PRESETS[parsed.preset].w, OUTPUT_PRESETS[parsed.preset].h), { commit: true });
    pristineRef.current = false;
    setActiveTemplateId(tpl._id || tpl.template_id || null);
    setTemplateName(tpl.name || '');
    setSelected(null);
    toast.success(`Loaded "${tpl.name || 'Untitled'}"`);
  };

  const deleteTemplate = async (tpl) => {
    if (!tpl._id) return;
    try {
      await deleteCompositionTemplate(tpl._id);
      const next = savedTemplates.filter((t) => t._id !== tpl._id);
      setSavedTemplates(next);
      saveLocalTemplates(user, next);
      if (activeTemplateId === tpl._id) setActiveTemplateId(null);
      toast.success('Template deleted');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete template');
    }
  };

  // ── submit / retry / reset ────────────────────────────────
  const canGenerate = job?.status === 'awaiting_dreamface' || job?.status === 'completed' || job?.status === 'failed';
  const canRetry = Boolean(job?.recovery?.can_retry) || job?.status === 'processing' || job?.status === 'failed';
  const isLikelyStuck = Boolean(job?.recovery?.is_likely_stuck);
  const bgNeedsUpload = ['image', 'video'].includes(design.background.type) && !design.background.url;

  const handleRetry = async (force = false) => {
    if (!job) return;
    setRetrying(true);
    try {
      const avatarPayload = job.batches.map((b) => ({
        batch_index: b.batch_index,
        url: (avatarLinks[b.batch_index] || b.avatar_url || '').trim(),
      }));
      await api.post(`/dreamface/jobs/${jobId}/retry`, {
        layout_config: layoutConfig,
        avatar_links: avatarPayload,
        force,
      }, { headers: { Authorization: `Bearer ${user.token}` } });
      toast.success(force ? 'Render force-restarted' : 'Render re-queued');
      await loadJob();
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to retry render';
      if (err.response?.status === 409 && !force) toast.warn(`${msg} — use Force retry if the job is stuck.`);
      else toast.error(msg);
    } finally {
      setRetrying(false);
    }
  };

  const handleReset = async () => {
    if (!job || job.status !== 'processing') return;
    if (!window.confirm('Reset this job so you can edit avatar links? It will not start rendering until you click Generate again.')) return;
    setResetting(true);
    try {
      await api.post(`/dreamface/jobs/${jobId}/reset`, { status: 'awaiting_dreamface' }, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      toast.success('Job reset — you can edit links and generate again');
      await loadJob();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to reset job');
    } finally {
      setResetting(false);
    }
  };

  const handleSubmit = async () => {
    if (!job) return;
    const missing = job.batches.filter((b) => !avatarLinks[b.batch_index]?.trim());
    if (missing.length > 0) {
      toast.error(`Missing avatar links for batch${missing.length > 1 ? 'es' : ''}: ${missing.map((b) => b.batch_index + 1).join(', ')}`);
      setLinksOpen(true);
      return;
    }
    if (bgUploading) { toast.error('Please wait for the background upload to finish'); return; }
    if (bgNeedsUpload) { toast.error(`Upload a background ${design.background.type} or pick another fill`); setTab('background'); return; }
    setSubmitting(true);
    try {
      const headers = { Authorization: `Bearer ${user.token}` };
      const avatarPayload = job.batches.map((b) => ({ batch_index: b.batch_index, url: (avatarLinks[b.batch_index] || '').trim() }));
      if (job.status === 'awaiting_dreamface') {
        await api.post('/dreamface/submit-links', { job_id: jobId, avatar_links: avatarPayload, layout_config: layoutConfig }, { headers });
        toast.success('Avatar links submitted — video generation started!');
      } else if (job.status === 'completed' || job.status === 'failed') {
        await api.post(`/dreamface/jobs/${jobId}/re-render`, { layout_config: layoutConfig, avatar_links: avatarPayload }, { headers });
        toast.success('Video generation started with your saved composition!');
      } else {
        toast.error(`Cannot generate while job status is: ${job.status}`);
        return;
      }
      setTimeout(() => navigate('/processing-drafts'), 1200);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to start video generation');
    } finally {
      setSubmitting(false);
    }
  };

  // ── render ────────────────────────────────────────────────
  if (loading) return <div className="th-loading"><Loader /></div>;
  if (!job) {
    return (
      <div className="th-error">
        <h2>Job not found</h2>
        <button type="button" onClick={() => navigate(-1)}><FaArrowLeft /> Go Back</button>
      </div>
    );
  }

  const batches = job.batches || [];
  const allLinksProvided = batches.every((b) => avatarLinks[b.batch_index]?.trim());
  const completedBatches = batches.filter((b) => avatarLinks[b.batch_index]?.trim()).length;
  const modeKeys = modeLayerKeys(design, mode);

  const linksPanel = (
    <aside className={`ths-links ${linksOpen ? 'is-open' : ''}`} aria-label="Avatar links">
      <div className="ths-links__head">
        <div>
          <strong>Avatar links</strong>
          <em>{completedBatches} / {batches.length} ready</em>
        </div>
        <button type="button" className="st-icon-btn ths-links__close" onClick={() => setLinksOpen(false)} aria-label="Close avatar links"><FaTimes /></button>
      </div>
      <div className="ths-progress"><span style={{ width: `${batches.length ? (completedBatches / batches.length) * 100 : 0}%` }} /></div>
      <div className="ths-batches">
        {batches.map((batch) => {
          const hasLink = Boolean(avatarLinks[batch.batch_index]?.trim());
          const resolving = resolvingBatches[batch.batch_index] && hasLink;
          return (
            <div key={batch.batch_index} className={`ths-batch ${hasLink ? 'is-linked' : ''}`}>
              <div className="ths-batch__top">
                <span className="ths-batch__num">Batch {batch.batch_index + 1}</span>
                <span className="ths-batch__meta">{Number(batch.duration_sec || 0).toFixed(1)}s · {batch.segment_indices.length} seg</span>
                <a
                  href={batch.download_url}
                  download
                  className="ths-batch__dl"
                  title="Download audio for DreamFace"
                  onClick={() => toast.info(`Downloading batch ${batch.batch_index + 1}…`)}
                >
                  <FaDownload /><span>Audio</span>
                </a>
              </div>
              <label className="ths-batch__input">
                <FaLink aria-hidden="true" />
                <input
                  type="url"
                  placeholder="Paste DreamFace video link…"
                  value={avatarLinks[batch.batch_index] || ''}
                  onChange={(e) => setAvatarLinks((p) => ({ ...p, [batch.batch_index]: e.target.value }))}
                  aria-label={`Avatar link for batch ${batch.batch_index + 1}`}
                />
                {resolving ? <span className="th-spinner" aria-label="Checking link" /> : hasLink && <FaCheck className="ths-ok" aria-hidden="true" />}
              </label>
            </div>
          );
        })}
      </div>
    </aside>
  );

  return (
    <div className="ths th-studio">
      {/* ── header ── */}
      <header className="ths-header">
        <div className="ths-header__title">
          <button type="button" className="th-back" onClick={() => navigate(-1)} aria-label="Go back"><FaArrowLeft /></button>
          <MdFaceRetouchingNatural className="ths-header__icon" aria-hidden="true" />
          <div className="ths-header__copy">
            <h1>Talking Heads Studio</h1>
            <p>
              <span>Job {String(jobId).slice(-8)}</span>
              <span className={`th-status th-status--${job.status}`}>{String(job.status).replace(/_/g, ' ')}</span>
              <span className="ths-header__canvas">{design.name}</span>
            </p>
          </div>
        </div>
        <div className="ths-header__actions">
          <div className="ths-undo" role="group" aria-label="History">
            <button type="button" className="st-icon-btn" onClick={history.undo} disabled={!history.canUndo} title="Undo (Ctrl+Z)" aria-label="Undo"><FaUndo /></button>
            <button type="button" className="st-icon-btn" onClick={history.redo} disabled={!history.canRedo} title="Redo (Ctrl+Shift+Z)" aria-label="Redo"><FaRedo /></button>
          </div>
          {job.status === 'processing' && (
            <>
              <button type="button" className="th-reset-btn" onClick={handleReset} disabled={resetting || retrying}>
                {resetting ? 'Resetting…' : 'Reset job'}
              </button>
              <button
                type="button"
                className={`th-retry-btn ${isLikelyStuck ? 'th-retry-btn--urgent' : ''}`}
                onClick={() => handleRetry(true)}
                disabled={retrying || resetting || !canRetry}
              >
                <FaRedo aria-hidden="true" /> {retrying ? 'Retrying…' : isLikelyStuck ? 'Force retry' : 'Retry render'}
              </button>
            </>
          )}
          <button type="button" className="th-edit-comp-btn" onClick={() => setCompositionOpen(true)} title="Edit tags, clips and images without re-recording narration">
            <MdMovie aria-hidden="true" /><span className="th-btn-label">Edit Composition</span>
          </button>
          <button
            type="button"
            className={`th-submit-btn ${allLinksProvided && canGenerate ? 'th-submit-btn--ready' : ''}`}
            onClick={handleSubmit}
            disabled={submitting || !canGenerate}
            title={!allLinksProvided ? 'Paste every avatar link first' : undefined}
          >
            {submitting
              ? <><span className="th-spinner" aria-hidden="true" /> <span className="th-btn-label">Submitting…</span></>
              : <><FaCheck aria-hidden="true" /> <span className="th-btn-label">Generate Video</span></>}
          </button>
        </div>
      </header>

      {job.status === 'processing' && (
        <div className={`th-recovery-banner ${isLikelyStuck ? 'th-recovery-banner--stuck' : ''}`}>
          <div>
            <strong>Video is rendering</strong>
            {job.recovery?.processing_seconds > 0 && (
              <span className="th-recovery-meta">
                {' '}· running {Math.floor(job.recovery.processing_seconds / 60)}m{job.retry_count ? ` · retry #${job.retry_count}` : ''}
              </span>
            )}
            {isLikelyStuck && (
              <p className="th-recovery-hint">No progress for a while? Use <strong>Force retry</strong>, or <strong>Reset job</strong> to edit avatar links first.</p>
            )}
          </div>
        </div>
      )}
      {job.status === 'failed' && job.error && (
        <div className="th-recovery-banner th-recovery-banner--failed">
          <div>
            <strong>Render failed</strong>
            <p className="th-recovery-hint">{job.error}</p>
          </div>
          <button type="button" className="th-retry-btn th-retry-btn--urgent" onClick={() => handleRetry(true)} disabled={retrying}>
            <FaRedo aria-hidden="true" /> Retry render
          </button>
        </div>
      )}

      {/* ── body ── */}
      <div className="ths-body">
        {linksPanel}
        {linksOpen && <button type="button" className="ths-scrim" aria-label="Close avatar links" onClick={() => setLinksOpen(false)} />}

        <main className="ths-center" style={{ '--ths-ar': W / H }}>
          <div className="ths-toolbar">
            <button type="button" className={`ths-links-btn ${allLinksProvided ? 'is-done' : ''}`} onClick={() => setLinksOpen(true)}>
              <FaLink /> Links <b>{completedBatches}/{batches.length}</b>
            </button>
            <div className="st-seg ths-mode" role="radiogroup" aria-label="Preview">
              <button type="button" role="radio" aria-checked={mode === 'narration'} className={mode === 'narration' ? 'is-active' : ''} onClick={() => switchMode('narration')}>
                <MdFaceRetouchingNatural /> <span>Narration</span>
              </button>
              <button type="button" role="radio" aria-checked={mode === 'clip'} className={mode === 'clip' ? 'is-active' : ''} onClick={() => switchMode('clip')}>
                <MdMovie /> <span>Clip</span>
              </button>
            </div>
            <div className="st-seg ths-format" role="radiogroup" aria-label="Output format">
              {Object.entries(OUTPUT_PRESETS).map(([k, p]) => (
                <button key={k} type="button" role="radio" aria-checked={outputPreset === k} className={outputPreset === k ? 'is-active' : ''} onClick={() => changeOutput(k)} title={p.long}>
                  {p.label}
                </button>
              ))}
            </div>
            <span className="ths-toolbar__spacer" />
            <button type="button" className={`st-icon-btn ${snap ? 'is-active' : ''}`} onClick={() => setSnap((v) => !v)} title={snap ? 'Snapping on (hold Alt to skip)' : 'Snapping off'} aria-pressed={snap} aria-label="Snapping">
              <FaMagnet />
            </button>
            <button
              type="button"
              className={`st-icon-btn ${contentSample ? 'is-active' : ''}`}
              onClick={() => (contentSample ? setContentSample(null) : sampleInputRef.current?.click())}
              title={contentSample ? 'Remove preview sample' : 'Preview with a sample image (not uploaded)'}
              aria-label="Preview sample image"
            >
              <FaImage />
            </button>
            <input
              ref={sampleInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) setContentSample(URL.createObjectURL(f)); }}
            />
          </div>

          <ThStage
            design={design}
            update={update}
            checkpoint={history.checkpoint}
            mode={mode}
            W={W}
            H={H}
            media={media}
            selected={selected}
            onSelect={select}
            snap={snap}
            onEditText={() => { setTab('edit'); setTimeout(() => textRef.current?.focus(), 60); }}
          />

          <div className="ths-layerbar" role="toolbar" aria-label="Boxes">
            {modeKeys.map((k) => (
              <button
                key={k}
                type="button"
                className={`ths-layerchip ths-layerchip--${k} ${selected?.kind === 'layer' && selected.id === k ? 'is-active' : ''}`}
                onClick={() => select({ kind: 'layer', id: k })}
              >
                {k === 'avatar' ? <MdFaceRetouchingNatural /> : k === 'content' ? <MdVideoLibrary /> : <MdMovie />}
                {LAYER_META[k].label}
                <em>{design.layers[k].width}×{design.layers[k].height}</em>
              </button>
            ))}
            <span className="ths-layerbar__hint">
              {avatarLinkResolving && !primaryAvatarStreamUrl ? 'Loading avatar preview…' : mode === 'clip' ? 'Clip segments — no avatar' : 'Drag boxes · corners resize · arrows nudge'}
            </span>
          </div>
        </main>

        <aside className="ths-inspector" aria-label="Inspector">
          <nav className="ths-tabs" role="tablist">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={tab === t.id}
                className={tab === t.id ? 'is-active' : ''}
                onClick={() => setTab(t.id)}
              >
                {t.icon}<span>{t.label}</span>
              </button>
            ))}
          </nav>
          <div className="ths-inspector__body">
            {tab === 'canvases' && <CanvasGallery W={W} H={H} currentId={pristineRef.current ? design.canvasId : null} onApply={applyPremade} />}
            {tab === 'edit' && (
              selected?.kind === 'layer' ? (
                <LayerInspector design={design} layerKey={selected.id} W={W} H={H} update={update} mode={mode} avatarDims={primaryAvatarDims} onSelect={select} />
              ) : selected?.kind === 'decor' ? (
                <DecorInspector design={design} id={selected.id} update={update} W={W} H={H} textRef={textRef} onSelect={select} />
              ) : (
                <div className="ths-panel ths-pick">
                  <p className="ths-note">Select something on the canvas, or pick a box:</p>
                  {['content', 'avatar', 'clip'].map((k) => (
                    <button key={k} type="button" className="ths-pick__item" disabled={!design.layers[k]} onClick={() => select({ kind: 'layer', id: k }, true)}>
                      <span className="ths-dot" style={{ background: LAYER_META[k].color }} />
                      <strong>{LAYER_META[k].label}</strong>
                      <em>{design.layers[k] ? LAYER_META[k].hint : 'not used by this canvas'}</em>
                    </button>
                  ))}
                  <button type="button" className="ths-pick__item" onClick={() => setTab('decor')}>
                    <FaLayerGroup className="ths-pick__icon" />
                    <strong>Decorations</strong>
                    <em>{design.decor.length} on this canvas</em>
                  </button>
                </div>
              )
            )}
            {tab === 'background' && (
              <BackgroundInspector design={design} update={update} onUpload={handleBgUpload} uploading={bgUploading} layoutKind={design.layoutKind} />
            )}
            {tab === 'decor' && (
              <DecorList design={design} update={update} selectedId={selected?.kind === 'decor' ? selected.id : null} onSelect={select} H={H} W={W} />
            )}
            {tab === 'templates' && (
              <TemplatesPanel
                templates={savedTemplates}
                loading={templatesLoading}
                activeId={activeTemplateId}
                name={templateName}
                setName={setTemplateName}
                onSave={() => saveTemplate(false)}
                onSaveNew={() => saveTemplate(true)}
                onLoad={loadTemplate}
                onDelete={deleteTemplate}
                toDesign={templateToDesign}
                layoutConfig={layoutConfig}
              />
            )}
          </div>
        </aside>
      </div>

      {submitting && <Loader />}

      <DreamFaceCompositionPanel
        jobId={jobId}
        open={compositionOpen}
        onClose={() => setCompositionOpen(false)}
        user={user}
        jobStatus={job?.status}
        orientation={job?.orientation || 'landscape'}
      />
    </div>
  );
}
